

























































































































import axios from 'axios';
import i18n from 'vue-i18n';
import EventBus from '@/eventBus';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';
import CoCheckbox from '@/components/Atoms/co-checkbox/CoCheckbox.vue';
import CoHeader from '@/components/Molecules/co-header/CoHeader.vue';
import CoCard from '@/components/Molecules/co-card/CoCard.vue';
import CoCollapsible from '@/components/Molecules/co-collapsible/CoCollapsible.vue';
import CoMultiSelect from '@/components/Molecules/co-multiselect/CoMultiSelect.vue';

type PermissionAction = 'events.create' | 'pages.create' | 'feed.post';
type PlanOption = { Name: string; Value: string };
type PermissionPolicy = {
    action: PermissionAction;
    // Gateway uses snake_case for this API field.
    // eslint-disable-next-line camelcase
    allow_conditions: Array<{ field: string; values: string[] }>;
};
type PermissionForm = {
    action: PermissionAction;
    members: boolean;
    restrictPlans: boolean;
    selectedPlans: PlanOption[];
};
const actions: PermissionAction[] = ['events.create', 'pages.create', 'feed.post'];

export default {
    name: 'SpacePermissions',
    components: { CoButton, CoCheckbox, CoHeader, CoCard, CoCollapsible, CoMultiSelect },
    data() {
        return { policies: [] as PermissionForm[], policyPlans: [] as PlanOption[], savedPayload: '', loading: false };
    },
    computed: {
        planSlug(): string {
            return (
                this.$store?.state?.space?.PlanSlug ||
                this.$store?.state?.space_configuration_admin?.PlanSlug ||
                'unknown'
            )
                .toLowerCase()
                .trim();
        },
        canManagePermissions(): boolean {
            // Limits are the entitlement source; plan names are used only for paywall copy.
            return this.$store?.state?.limits?.Config?.manage_permissions === true;
        },
        hasChanges(): boolean {
            return this.savedPayload !== '' && JSON.stringify(this.toApi()) !== this.savedPayload;
        },
        hasMissingPlans(): boolean {
            return this.policies.some(this.needsPlanSelection);
        },
    },
    mounted() {
        this.getPermissions();
    },
    watch: {
        '$route.hash': 'openLinkedSection',
        canManagePermissions: 'getPermissions',
    },
    methods: {
        showPaywall() {
            EventBus.$emit('PAYWALL', { feature: 'permissions', plan: this.planSlug });
        },
        // Capture before child controls mutate their state, including keyboard and arrow-key selection.
        guardPermissionInteraction(event: MouseEvent | KeyboardEvent) {
            if (this.canManagePermissions || this.loading) return;
            if (
                event.type === 'keydown' &&
                ![' ', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(
                    (event as KeyboardEvent).key
                )
            )
                return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.showPaywall();
        },
        // A restricted member audience must have a plan to preserve its selection after saving.
        needsPlanSelection(policy: PermissionForm): boolean {
            return policy.members && policy.restrictPlans && policy.selectedPlans.length === 0;
        },
        async openLinkedSection() {
            // Loaded policies must render first; Vue 2 stores refs inside v-for as arrays.
            await this.$nextTick();
            // Reuse the collapsible's show method so links never toggle an already open section closed.
            const section = this.$refs[this.$route.hash.slice(1)]?.[0];
            if (!section?.show) return;
            section.show();
            await this.$nextTick();
            section.$el.scrollIntoView({ block: 'start' });
        },
        // Arrow keys select and focus the other radio without affecting the outer audience panel.
        switchMemberAudience(event: KeyboardEvent) {
            if (this.loading) return;
            const group = event.currentTarget as HTMLElement;
            const next = group.querySelector('[role="radio"][aria-checked="false"]') as HTMLElement;
            next.click();
            next.focus();
        },
        actionLabel(action: PermissionAction) {
            return this.$t(
                {
                    'events.create': 'labels.permissionsActions.eventsCreate',
                    'pages.create': 'labels.permissionsActions.pagesCreate',
                    'feed.post': 'labels.permissionsActions.feedPost',
                }[action]
            );
        },
        // Map API alternatives into the audience and optional plan restriction once on load.
        fromApi(policies: PermissionPolicy[]): PermissionForm[] {
            return actions.map((action) => {
                const policy = policies.find((item) => item.action === action);
                const conditions = policy?.allow_conditions || [];
                const allMembers =
                    !conditions.length ||
                    conditions.some(
                        (condition) =>
                            condition.field === 'role' &&
                            condition.values.some((role) => ['user', 'member'].includes(role))
                    );
                const planIds = conditions
                    .filter((condition) => condition.field === 'plan_id')
                    .reduce((ids: string[], condition) => ids.concat(condition.values), []);
                return {
                    action,
                    members: allMembers || planIds.length > 0,
                    restrictPlans: !allMembers && planIds.length > 0,
                    selectedPlans: planIds.map(
                        (id) => this.policyPlans.find((plan) => plan.Value === id) || { Name: id, Value: id }
                    ),
                };
            });
        },
        // Plans replace the member role alternative so unrestricted members cannot bypass the restriction.
        toApi(): PermissionPolicy[] {
            return this.policies.map((policy) => ({
                action: policy.action,
                allow_conditions: [
                    { field: 'role', values: policy.members && !policy.restrictPlans ? ['admin', 'user'] : ['admin'] },
                    ...(policy.members && policy.restrictPlans
                        ? [{ field: 'plan_id', values: policy.selectedPlans.map((plan) => plan.Value).sort() }]
                        : []),
                ],
            }));
        },
        async getPermissions() {
            this.loading = true;
            try {
                const [permissions, plans] = await Promise.all([
                    axios.get('/admin/space/permissions'),
                    axios.get('/admin/plan/list'),
                ]);
                this.policyPlans = (plans.data.Plans || []).map((plan) => ({ Name: plan.Name, Value: plan.ID }));
                this.policies = this.fromApi(permissions.data);
                this.savedPayload = JSON.stringify(this.toApi());
            } catch (error) {
                EventBus.$emit('ERROR', { Message: this.$t('messages.permissions.updateFailed') });
                console.error(error);
            } finally {
                this.loading = false;
                this.openLinkedSection();
            }
        },
        async setPermissions() {
            if (this.loading) return;
            // Keep Save clickable for locked plans so it enters the shared upgrade flow.
            if (!this.canManagePermissions) {
                this.showPaywall();
                return;
            }
            if (!this.hasChanges || this.hasMissingPlans) return;
            this.loading = true;
            try {
                const response = await axios.post('/admin/space/permissions', this.toApi());
                this.policies = this.fromApi(response.data);
                this.savedPayload = JSON.stringify(this.toApi());
                EventBus.$emit('INFO', { Message: this.$t('messages.permissions.updateSuccess') });
            } catch (error) {
                EventBus.$emit('ERROR', { Message: this.$t('messages.permissions.updateFailed') });
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
    },
};
